import { alsApp } from './betriebsart'

/**
 * Was für ein Gerät ist das — und zwar nur da, wo es wirklich darauf ankommt.
 *
 * **Das Gerät zu erfragen ist die Ausnahme und keine Gewohnheit.** Fast alles
 * in dieser Anwendung entscheidet sich an einer Fähigkeit („kann dieser
 * Browser Dateien teilen?"), nicht an einem Namen — eine Kennung lügt, veraltet
 * und trifft das nächste Gerät falsch. Es gibt aber Fälle, in denen zwei
 * Systeme *dieselbe* Fähigkeit haben und mit ihr **etwas anderes tun**. Dann
 * hilft keine Abfrage der Fähigkeit weiter, und die Stelle gehört benannt
 * statt versteckt.
 *
 * Genau diese Datei ist der Ort dafür. Wer hier etwas ergänzt, schreibt dazu,
 * welche zwei Verhalten er auseinanderhält.
 */

/**
 * Ein iPhone oder ein iPad — Apple-System mit Finger.
 *
 * **Nicht über den Namen allein.** Ein iPad meldet sich seit iPadOS 13 als
 * „Macintosh"; wer nur nach `iPad` sucht, hält es für einen Rechner. Und ein
 * echter Mac meldet **null** Berührpunkte — daran, und nur daran, lassen sich
 * die beiden auseinanderhalten. Beides zusammen trifft genau die Geräte, um
 * die es geht.
 *
 * **Größer als null und nicht größer als eins.** Ein iPhone meldet fünf, das
 * war die erste Version; ein Fenster mit nachgestellter Berührung meldet aber
 * eine, und damit fiel der Prüfstand durch das Raster. Die Zahl ist hier auch
 * gar nicht die Frage — gefragt ist „hat dieses Gerät einen Finger", und das
 * beantwortet jede Zahl über null. Ein Rechner mit Berührbildschirm trägt
 * kein `Mac` oder `iPhone` in seiner Kennung und fällt schon dort heraus.
 *
 * Gebraucht wird das an zwei Stellen, und beide Male aus demselben Grund:
 * dort tut Apple etwas anderes als der Rest.
 *
 * - **Sichern eines Fotos** (`lib/sichern.ts`): auf iOS legt das Teilen-Blatt
 *   das Bild in *Fotos*, ein Download landet dagegen in *Dateien*. Auf Android
 *   ist es umgekehrt.
 * - **Der Hinweis zum Installieren** (`SettingsPage`): Push gibt es dort nur
 *   für Web-Apps auf dem Home-Bildschirm.
 * - **Ob Push überhaupt geht** (`lib/push.ts`, seit 1.1.0 von hier statt mit
 *   einer eigenen Abfrage daneben): fehlt `PushManager`, heißt das auf iOS
 *   „erst auf den Home-Bildschirm", anderswo „dieser Browser kann es nicht".
 * - **Der Rand unter dem Glasband** (`glasbandOben()` unten, 1.53.1): iOS 27
 *   malt in der Home-Bildschirm-App ein Band über den oberen Rand des
 *   Inhalts, sonst niemand.
 */
export function istAppleMitFinger(): boolean {
  return navigator.maxTouchPoints > 0 && /Mac|iPad|iPhone|iPod/.test(navigator.userAgent)
}

/**
 * Does iOS draw its glass band over the top of the page? (#48, 1.53.1)
 *
 * **What this tells apart.** Since iOS 27 the system paints a "Liquid
 * Glass" scroll-edge band over the topmost ~44 points of a home-screen web
 * app -- over the app's own header, which then reads soft and pale. No CSS
 * or meta line switches it off, `env(safe-area-inset-top)` does not grow
 * with it, and painting a strip underneath stopped working with iOS 26.
 * That inset is 0 in this app anyway (no `black-translucent`, see
 * `index.html`) -- except after Apple's full-screen video player, when the
 * window covers the status bar; the margin from `lib/glasband.ts` is
 * therefore added to the inset, not compared with it (1.131.0).
 * iOS 26 (after #48: no translucent status bar, `theme_color`) leaves the
 * header alone; Safari in a tab, Android and every computer never had the
 * band. The one lever left is where the content lies -- so a margin is kept
 * free, and only where the band is (`lib/glasband.ts`).
 *
 * **The version is read from a capability, not from the user agent.** Since
 * iOS 26 the OS number in the user agent is frozen, and an iPad calls
 * itself "Macintosh" anyway. `:heading` is a CSS pseudo-class that shipped
 * with Safari 27; WebKit that knows it is WebKit 27 or newer. Chromium knows
 * it too -- that is why the Apple-with-finger question comes first.
 *
 * **Wiedervorlage:** an iOS that stops drawing the band would still match
 * here and keep the margin. When that day comes, this is the line.
 */
export function glasbandOben(): boolean {
  return (
    istAppleMitFinger()
    && alsApp()
    && typeof CSS !== 'undefined'
    && typeof CSS.supports === 'function'
    && CSS.supports('selector(:heading)')
  )
}

/**
 * Ein Wort für die Liste der Passkeys — was der Browser über sich sagt.
 *
 * Kein Verhalten hängt daran, nur ein Name: „iPad", „Windows", damit man in
 * der Liste der angemeldeten Geräte das eigene wiederfindet. `null`, wenn
 * nichts zu erkennen ist; das Wort dafür kommt aus den Texten. Es steht hier
 * und nicht in `lib/passkey.ts`, weil jede Kennung an einer Stelle gelesen
 * wird (review 1.1.0) — ändert Apple, wie ein iPad sich meldet, ist das eine
 * Zeile und nicht drei.
 */
export function geraeteName(): string | null {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  // An iPad with Safari calls itself Macintosh -- the finger gives it away.
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Android/.test(ua)) return 'Android'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Linux/.test(ua)) return 'Linux'
  return null
}
